import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { staffApi, formatApiError } from "@/lib/api";
import { publishPresence } from "@/lib/liveSync";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sparkles, Users, Info, Lock, LockOpen, UserPlus } from "lucide-react";
import { substitutionOptionsForItem } from "@/lib/PackageEngine";
import { pad, baliParts, baliIsoFromLocal } from "./timeUtils";
import { WEEKDAY_KEY, DAY_OFF_MODES } from "./constants";
import ServiceCombobox from "@/components/forms/ServiceCombobox";
import CustomerCombobox from "@/components/forms/CustomerCombobox";
import PhoneInput from "@/components/forms/PhoneInput";
import { collectAlerts } from "@/pages/staff/brand/GuestPreferences";
import { branchThemeStyle } from "@/lib/branchTheme";

// Did the solo-edit PATCH actually change anything? Compares the
// appointment AS STORED (the PATCH response) against the record the modal
// opened on, so the save can never claim "Appointment updated" when the
// operator re-picked identical values.
const soloEditChanged = (before = {}, after = {}) => {
  const sameTime = (a, b) => {
    if (!a || !b) return a === b;
    const ta = new Date(a).getTime();
    const tb = new Date(b).getTime();
    return Number.isNaN(ta) || Number.isNaN(tb) ? a === b : ta === tb;
  };
  if (!sameTime(before.start_iso, after.start_iso)) return true;
  if ((before.service_id || "") !== (after.service_id || "")) return true;
  if ((before.staff_id || "") !== (after.staff_id || "")) return true;
  if ((before.room_id || "") !== (after.room_id || "")) return true;
  if ((before.customer_id || "") !== (after.customer_id || "")) return true;
  if ((before.notes || "") !== (after.notes || "")) return true;
  if (!!before.locked_to_staff !== !!after.locked_to_staff) return true;
  if ((before.duration_min || 0) !== (after.duration_min || 0)) return true;
  const beforeAssist = [...(before.assist_staff_ids || [])].sort().join(",");
  const afterAssist = [...(after.assist_staff_ids || [])].sort().join(",");
  return beforeAssist !== afterAssist;
};


// Rebuilt booking dialog — single-panel layout, searchable service/package
// picker, live customer typeahead with auto-create-on-save for new guests,
// and an optional Source picker that awards +200 loyalty points to the
// referrer (once, atomically). Time is displayed as friendly HH:MM in Bali
// local, backed by an ISO round-trip via `baliIsoFromLocal`.
export default function NewAppointmentModal({
  open, onOpenChange, branchId, branch = null, staffList, services, packages = [], rooms, prefill, onCreated,
  // appointmentSettings is retained for future warnings (deposit reqs etc.).
  appointmentSettings, // eslint-disable-line no-unused-vars
}) {
  const [busy, setBusy] = useState(false);
  // Selection { kind: 'service'|'package', id } for the picker
  const [selection, setSelection] = useState(null);
  // When a package is picked, the operator must also choose one of its
  // constituent services — this booking uses that service (backend requires
  // service_id) and stamps `package_id` for redemption tracking.
  const [packageServiceId, setPackageServiceId] = useState("");
  const [staffId, setStaffId] = useState("");
  // Additional practitioners for services with `required_staff` > 1. Indexed
  // 0..N-2 where N = required_staff (position 0 is Staff 2, position 1 is
  // Staff 3, etc.). Same filtered list drives every dropdown.
  const [assistStaffIds, setAssistStaffIds] = useState([]);
  // Customer-requested staff. Renders the lock icon on the calendar block
  // and blocks accidental staff swaps via drag/edit downstream.
  const [locked, setLocked] = useState(false);
  // Filtered staff list: staff who can perform the currently-picked service
  // AND are on-duty today. Refreshed whenever service/date changes.
  const [eligibleStaff, setEligibleStaff] = useState(null);  // null = not yet loaded
  const [staffLoading, setStaffLoading] = useState(false);
  const [roomId, setRoomId] = useState("");
  const [startIso, setStartIso] = useState("");   // canonical UTC ISO string
  const [notes, setNotes] = useState("");

  // Client — either an existing profile (has id) OR a sentinel `{__new:true}`
  // marker while the operator fills in the new-customer fields below.
  const [client, setClient] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");

  // Referral source — a picked customer OR null. The chip in CustomerCombobox
  // handles both the search + selected-state UI.
  const [source, setSource] = useState(null);
  // When the picked client ALREADY has a `referred_by_customer_id` on file,
  // we auto-populate the source from that profile and lock the field. Staff
  // manage referrer edits from the customer profile page (which is where the
  // "no double-award" guard rail is explained). New/first-time customers keep
  // the picker interactive so the operator can credit the referrer.
  const [sourceLocked, setSourceLocked] = useState(false);

  // Staff-only booking alert popup (fires once when a customer with a set
  // booking_alert is picked or pre-filled).
  const [bookingAlert, setBookingAlert] = useState(null);

  // ── Iter-254 · Transportation manual form state ──────────────────────
  // When the picked service belongs to the "Transportation" category the
  // wizard flips into a driver-sign mode: the client/source pickers are
  // hidden and the operator fills a manual form (Full Name, Hotel/Villa,
  // Address, Contact Phone). The existing time input doubles as the
  // "Treatment Time". Nothing here touches the customers collection —
  // the appointment persists with `is_transportation=true`, a null
  // `customer_id`, and a `transport_meta` blob for the print slip.
  const [transportName, setTransportName] = useState("");
  const [transportHotel, setTransportHotel] = useState("");
  const [transportAddress, setTransportAddress] = useState("");
  const [transportPhone, setTransportPhone] = useState("");

  // Live-sync presence useEffect moved below the wizard state
  // declarations to fix a TDZ ReferenceError (`Cannot access 'isWizard'
  // before initialization`) — the dependency array was being evaluated
  // during render before `isWizard` / `wizardSteps` / `wizardIndex`
  // were declared. See the block right after `const isWizard = …`.

  // Reset everything when the modal is (re)opened, honouring `prefill`.
  useEffect(() => {
    if (!open) return;
    setDeletedStepIds([]);
    // Linked-visit edit path: prefill.linkedSiblings carries every sibling
    // of the visit (package OR ad-hoc). Seed the wizard with each sibling
    // as a step so the operator sees the whole itinerary and can edit any
    // slot. Fall through to the flat prefill path when there's no group.
    const sibs = (prefill?.editing && Array.isArray(prefill?.linkedSiblings) && prefill.linkedSiblings.length > 0)
      ? prefill.linkedSiblings
      : null;
    if (sibs) {
      const clickedIdx = Math.max(0, sibs.findIndex((s) => s.id === prefill.id));
      // Resolve each sibling's ORIGINAL package item (by index) so the
      // substitution dropdown offers the correct alternatives even
      // when the operator has already swapped the delivered service
      // on a prior save. Matching order:
      //   1. Position match — sibling index i maps to package item i.
      //   2. Direct id match — item.service_id === sibling.service_id.
      //   3. Substitution match — item.substitutions includes sibling.service_id.
      const pkgForPrefill = prefill?.package_id
        ? (packages || []).find((p) => p.id === prefill.package_id)
        : null;
      const pkgItems = pkgForPrefill?.items || [];
      const resolveOriginalForSibling = (sib, idx) => {
        // Positional map wins when the sibling count matches the
        // package's item count — the wizard rebuilds strictly in
        // package-item order on create.
        if (pkgItems.length === sibs.length && pkgItems[idx]) {
          return { originalId: pkgItems[idx].service_id, itemIndex: idx };
        }
        const directIdx = pkgItems.findIndex((it) => it.service_id === sib.service_id);
        if (directIdx >= 0) return { originalId: pkgItems[directIdx].service_id, itemIndex: directIdx };
        const subIdx = pkgItems.findIndex((it) => Array.isArray(it.substitutions) && it.substitutions.includes(sib.service_id));
        if (subIdx >= 0) return { originalId: pkgItems[subIdx].service_id, itemIndex: subIdx };
        return { originalId: sib.service_id, itemIndex: -1 };
      };
      const steps = sibs.map((s, i) => {
        const { originalId, itemIndex } = pkgForPrefill
          ? resolveOriginalForSibling(s, i)
          : { originalId: s.service_id, itemIndex: -1 };
        return {
          id: s.id, // marker: save path PATCHes existing rows instead of POSTing new ones
          service_id: s.service_id,
          service_name: s.service_name,
          // `original_service_id` pins the wizard step to the
          // package item so the Substitutions dropdown can be
          // regenerated on every render — swapping back to the
          // original still works.
          original_service_id: originalId,
          package_item_index: itemIndex,
          duration_min: s.duration_min || 30,
          start_iso: s.start_iso,
          staff_id: s.staff_id,
          assist_staff_ids: s.assist_staff_ids || [],
          room_id: s.room_id || "",
          locked: !!s.locked_to_staff,
          // Each linked sibling carries its own notes on the backend. Keep
          // them per-step so switching / removing the active step doesn't
          // bleed one sibling's notes into another's PATCH payload.
          notes: s.notes || "",
        };
      });
      const clicked = steps[clickedIdx] || steps[0];
      setSelection(prefill?.package_id
        ? { kind: "package", id: prefill.package_id }
        : (clicked?.service_id ? { kind: "service", id: clicked.service_id } : null));
      setPackageServiceId("");
      setWizardSteps(steps);
      setWizardIndex(clickedIdx);
      // Package group → package wizard; ad-hoc group → multi-mode.
      setMultiMode(!prefill?.package_id);
      setStaffId(clicked.staff_id || (staffList[0]?.id ?? ""));
      setAssistStaffIds(clicked.assist_staff_ids || []);
      setLocked(!!clicked.locked);
      setEligibleStaff(null);
      setRoomId(clicked.room_id || "");
      setStartIso(clicked.start_iso || "");
      setNotes(clicked.notes || "");
      setSource(null);
      setSourceLocked(false);
      setBookingAlert(null);
    } else {
      setSelection(prefill?.service_id ? { kind: "service", id: prefill.service_id } : null);
      setPackageServiceId("");
      setWizardSteps([]);
      setWizardIndex(0);
      setMultiMode(false);
      setStaffId(prefill?.staff_id || (staffList[0]?.id ?? ""));
      setAssistStaffIds(prefill?.assist_staff_ids || []);
      setLocked(!!prefill?.locked_to_staff);
      setEligibleStaff(null);
      setRoomId(prefill?.room_id || "");
      setStartIso(prefill?.start_iso || "");
      setNotes(prefill?.notes || "");
      setSource(null);
      setSourceLocked(false);
      setBookingAlert(null);
    }

    // Prefilled customer (opened from a booked block): hydrate the picker
    // with the existing profile so the operator sees the same client already
    // linked without having to search again.
    if (prefill?.customer_id) {
      staffApi.get(`/customers/${prefill.customer_id}`)
        .then((r) => {
          const c = r.data?.customer;
          if (c) {
            setClient(c);
            hydrateSourceFromCustomer(c);
            const items = collectAlerts(c, "booking");
            if (items.length) {
              setBookingAlert({ name: c.full_name || c.email || "customer", items });
            }
          }
        })
        .catch(() => { /* silent */ });
    } else {
      setClient(null);
      setFirstName("");
      setLastName("");
      setEmail("");
      setWhatsapp("");
    }
    // Hydrate the Transportation manual-form fields when re-opening a
    // transportation tile (edit path). Otherwise reset to blank so a
    // fresh Transportation booking starts empty.
    const meta = prefill?.transport_meta || {};
    setTransportName(meta.full_name || prefill?.customer_name || "");
    setTransportHotel(meta.hotel || "");
    setTransportAddress(meta.address || "");
    setTransportPhone(meta.phone || "");
  }, [open, prefill]);
  // Deliberately omit `staffList` — this reset effect must only re-run when
  // the modal (re-)opens or the caller swaps the prefill target. Parent
  // refreshes (onCreated) mutate staffList and would otherwise clobber
  // in-flight wizard state (deletedStepIds, edited fields) while the
  // dialog is still open recovering from a partial-save failure.

  // Fire the booking-alert popup when the operator picks a customer whose
  // profile carries one. Deliberately separate from the effect above so it
  // triggers on later picks too.
  useEffect(() => {
    if (!client || client.__new) return;
    const items = collectAlerts(client, "booking");
    if (items.length) {
      setBookingAlert({ name: client.full_name || client.email || "customer", items });
    }
  }, [client]);

  // When the picked customer already has a `referred_by_customer_id`, fetch
  // that referrer's profile and lock the Source field to display it. The
  // backend's atomic guard means a second-time source pick can't re-award
  // loyalty anyway; locking the UI makes that explicit to the operator.
  const hydrateSourceFromCustomer = async (c) => {
    const refId = c?.referred_by_customer_id;
    if (!refId) { setSource(null); setSourceLocked(false); return; }
    try {
      const rr = await staffApi.get(`/customers/${refId}`);
      const ref = rr.data?.customer;
      if (ref) { setSource(ref); setSourceLocked(true); }
    } catch { /* silent — leave source empty */ }
  };

  // Derive the readable date + HH:MM from the ISO. When start_iso is empty
  // (e.g. modal opened via keyboard shortcut) we fall back to "—".
  const timePieces = useMemo(() => {
    if (!startIso) return { date: null, dateLabel: "—", timeLabel: "—", minutes: 0 };
    const d = new Date(startIso);
    const { h, m } = baliParts(startIso);
    return {
      // Local Bali date (yyyy-mm-dd) is the actual booking date. Building a
      // Date via new Date(iso) then extracting local getFullYear/etc. is
      // correct for BALI because we shift by +8 when composing the ISO
      // upstream. We rely on `baliIsoFromLocal(dateObj, minutes)` to
      // round-trip when the operator edits the HH:MM.
      date: d,
      dateLabel: d.toLocaleDateString("en-GB", {
        weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Makassar",
      }),
      timeLabel: `${pad(h)}:${pad(m)}`,
      minutes: h * 60 + m,
    };
  }, [startIso]);

  const onTimeChange = (val) => {
    // val is "HH:MM"
    if (!/^\d{2}:\d{2}$/.test(val)) return;
    const [hh, mm] = val.split(":").map((x) => parseInt(x, 10));
    if (hh > 23 || mm > 59) return;
    if (!timePieces.date) return;
    // Reconstruct a Date holding the SAME local Bali calendar day. We build
    // the date from Bali-local yyyy-mm-dd so DST-adjacent months don't shift
    // it around. Reusing the existing helper keeps the timezone logic in one
    // place (see calendar/timeUtils.js).
    const localDateStr = new Date(startIso).toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
    const [y, mo, d] = localDateStr.split("-").map((n) => parseInt(n, 10));
    const localDate = new Date(y, mo - 1, d);
    setStartIso(baliIsoFromLocal(localDate, hh * 60 + mm));
  };

  // ── Package wizard state ────────────────────────────────────────────
  // When the operator picks a package, we step through each of its
  // services in order. Each step captures its own time/staff/assist/room
  // and produces one appointment on save. Steps prior to `wizardIndex`
  // are "confirmed" (compact row); the step at `wizardIndex` is expanded
  // with full controls; steps after it are placeholders shown as arrows.
  // `wizardSteps[i]` = { service_id, service_name, duration_min, start_iso,
  //                      staff_id, assist_staff_ids, room_id, locked }.
  const [wizardSteps, setWizardSteps] = useState([]);
  const [wizardIndex, setWizardIndex] = useState(0);
  // Persisted step ids the operator removed during an edit session. These
  // must be silently DELETEd on save so the underlying appointment tiles
  // vanish from the calendar rather than merely disappearing from this
  // dialog's local state.
  const [deletedStepIds, setDeletedStepIds] = useState([]);
  // Ad-hoc multi-service flag: set true when the operator clicks "+ Add
  // Another Service" from single-service mode. Powers a wizard that isn't
  // tied to a package (each step's service is freely editable, no
  // package_id stamped on the POST). Persists until the modal closes.
  const [multiMode, setMultiMode] = useState(false);
  const isWizard = wizardSteps.length > 0 && (selection?.kind === "package" || multiMode);

  // Live-sync presence (Feb 2026): publish a `presence` claim so every
  // OTHER logged-in operator's calendar renders a perforated dashed
  // square on the exact staff column + slot this operator is drafting.
  // Republishes on every relevant edit (staff swap, time change,
  // service switch that changes the duration). The active claim is
  // released the instant the modal closes (whether via Save or
  // Cancel) so the peer squares vanish immediately.
  //
  // NOTE: This effect MUST be declared AFTER `wizardSteps`,
  // `wizardIndex`, `multiMode` and `isWizard` so its dependency array
  // (evaluated inline during render) doesn't hit a TDZ
  // ReferenceError.
  useEffect(() => {
    if (!open) { publishPresence(null); return; }
    const activeStep = isWizard ? (wizardSteps[wizardIndex] || {}) : null;
    const activeStaffId = activeStep?.staff_id || staffId;
    const activeStartIso = activeStep?.start_iso || startIso;
    const activeDuration = activeStep?.duration_min || 30;
    if (!activeStaffId || !activeStartIso || !branchId) { publishPresence(null); return; }
    let endIso = "";
    try {
      const end = new Date(new Date(activeStartIso).getTime() + activeDuration * 60000);
      endIso = end.toISOString();
    } catch { endIso = activeStartIso; }
    publishPresence({
      key: `${branchId}|${activeStaffId}|${activeStartIso}`,
      branch_id: branchId,
      staff_id: activeStaffId,
      start_iso: activeStartIso,
      end_iso: endIso,
    });
    return () => { publishPresence(null); };
  }, [open, branchId, staffId, startIso, isWizard, wizardSteps, wizardIndex]);

  // The picked package's items → drives the wizard's service list.
  const packageServices = useMemo(() => {
    if (selection?.kind !== "package") return [];
    const p = packages.find((x) => x.id === selection.id);
    if (!p) return [];
    const ids = (p.items || []).map((i) => i.service_id).filter(Boolean);
    return ids
      .map((sid) => services.find((s) => s.id === sid))
      .filter(Boolean);
  }, [selection, packages, services]);

  // The picked package doc — used to resolve per-step substitution
  // options via PackageEngine.substitutionOptionsForItem. Kept as its
  // own memo so the dropdown recomputes only when the operator swaps
  // packages, not on every keystroke.
  const activePackage = useMemo(() => {
    if (selection?.kind !== "package") return null;
    return packages.find((x) => x.id === selection.id) || null;
  }, [selection, packages]);

  // Swap the delivered service on step `stepIdx` to the picked
  // substitute id. `original_service_id` stays put so we can still
  // resolve the substitution list from the package. `package_id`
  // remains on the POST body (built in `save()` below) so the invoice
  // stays a single bundled item and pricing integrity is preserved by
  // PackageEngine's Stewart Gallacher emit. When the substitute has a
  // different length we recompute `duration_min` (service_time +
  // processing) so the calendar tile and cursor auto-advance line up.
  const swapStepService = (stepIdx, newServiceId) => {
    if (!newServiceId) return;
    const nextSvc = services.find((x) => x.id === newServiceId);
    if (!nextSvc) return;
    const svcTime = nextSvc.service_time_min || nextSvc.duration_min || 30;
    const procTime = Math.max(0, nextSvc.processing_time_min || 0);
    setWizardSteps((prev) => {
      const cur = prev[stepIdx];
      if (!cur) return prev;
      if (cur.service_id === newServiceId) return prev;
      const next = prev.slice();
      next[stepIdx] = {
        ...cur,
        service_id: newServiceId,
        service_name: nextSvc.name,
        duration_min: svcTime + procTime,
      };
      return next;
    });
    // Force the eligible-staff / hydration effects to re-run for the
    // freshly-picked service on the currently-active step.
    if (stepIdx === wizardIndex) {
      hydratedIndexRef.current = -1;
    }
  };

  // Clear the delivered service on step `stepIdx` so the picker falls back
  // to its search box and the operator can pick ANY treatment — not only the
  // substitutes the catalog declares. Without this the ✕ on a step's service
  // chip did nothing (the picker is controlled by `st.service_id`), so a
  // package step could never be swapped to an off-catalog treatment.
  const clearStepService = (stepIdx) => {
    setWizardSteps((prev) => {
      const cur = prev[stepIdx];
      if (!cur || !cur.service_id) return prev;
      const next = prev.slice();
      next[stepIdx] = { ...cur, service_id: "", service_name: "" };
      return next;
    });
    if (stepIdx === wizardIndex) {
      hydratedIndexRef.current = -1;
    }
  };

  // Effective service id — the current step's service in wizard mode, or the
  // plain picked service otherwise. Drives eligibleStaff fetch, requiredStaff
  // and validation.
  const effectiveServiceId = isWizard
    ? (wizardSteps[wizardIndex]?.service_id || "")
    : (selection?.kind === "service" ? selection.id : "");

  // Initialise the wizard when a package is picked (or the modal reopens on
  // a package selection). Each step starts with the service's baseline
  // duration and empty staff/room — the eligible-staff effect below fills
  // those in as the operator walks through. We intentionally do NOT depend
  // on `startIso` (would re-init and wipe operator progress on every time
  // edit) — the initial cursor is captured from the current startIso at
  // package-pick time only.
  const initialStartRef = useRef("");
  // Tracks which wizard index the flat state was last hydrated FROM.
  // Prevents the hydration effect (below) from re-running when wizardSteps
  // changes for reasons other than an index switch (e.g. snapshotStep or
  // eligibleStaff auto-picking a staffId inside step N).
  const hydratedIndexRef = useRef(-1);
  useEffect(() => {
    if (selection?.kind !== "package") {
      // Preserve the ad-hoc "+ Add Another Service" wizard — multiMode
      // deliberately keeps a non-package selection while wizardSteps are
      // populated, so we must NOT reset when it's active.
      if (multiMode) return;
      setWizardSteps([]);
      setWizardIndex(0);
      hydratedIndexRef.current = -1;
      return;
    }
    // Editing an EXISTING package visit — the open effect already seeded
    // `wizardSteps` from prefill.linkedSiblings (each step carrying its
    // appointment id). Skip the "seed fresh package steps" branch so the
    // clicked-sibling index isn't clobbered back to 0.
    if (wizardSteps.length > 0 && wizardSteps.every((s) => s.id)) return;
    if (packageServices.length === 0) return;
    setWizardSteps((prev) => {
      // Compare against `original_service_id` (falls back to `service_id`
      // on legacy seeds) so a substitution swap on an already-seeded
      // step never triggers a re-seed that would wipe the operator's
      // pick and reset staff/time back to defaults.
      if (
        prev.length === packageServices.length &&
        prev.every((s, i) => (s.original_service_id || s.service_id) === packageServices[i].id)
      ) {
        return prev;
      }
      const seed = startIso || prefill?.start_iso || "";
      initialStartRef.current = seed;
      let cursor = seed;
      // Preserve the clicked/prefilled primary specialist as the default
      // for EVERY step of the package. The eligible-staff effect will
      // downgrade to a fallback for any sub-service the lead can't
      // perform — but they stay on every service they qualify for.
      const leadStaffId = prefill?.staff_id || staffId || "";
      return packageServices.map((s, i) => {
        // Wizard cursor uses the TOTAL block (service_time + processing)
        // so each package sibling starts strictly after the previous
        // sibling's specialist/room is free — including the zebra
        // buffer that the guest never sees but the calendar reserves.
        const svcTime = s.service_time_min || s.duration_min || 30;
        const procTime = Math.max(0, s.processing_time_min || 0);
        const totalDur = svcTime + procTime;
        const step = {
          service_id: s.id,
          service_name: s.name,
          // Anchor to the package item so the substitution dropdown
          // can be resolved on every render — even after the operator
          // swaps the delivered service.
          original_service_id: s.id,
          package_item_index: i,
          duration_min: totalDur,
          start_iso: cursor,
          staff_id: leadStaffId,
          assist_staff_ids: [],
          room_id: "",
          locked: false,
          notes: "",
        };
        if (cursor) {
          const next = new Date(new Date(cursor).getTime() + (step.duration_min * 60000));
          cursor = next.toISOString();
        }
        return step;
      });
    });
    setWizardIndex(0);
    hydratedIndexRef.current = -1;
  }, [selection, packageServices, multiMode]);

  // Iter-380 · Missing-Assistant alert — when the parent opens this
  // modal with `prefill.focusStaff === true`, scroll the Staff
  // assignment section into view (not the top of the form) after the
  // dialog has mounted its content. The one-shot ref guards against
  // re-firing on every re-render while the modal stays open; it
  // resets whenever the modal closes so a follow-up open still scrolls.
  const focusedStaffForOpenRef = useRef(false);
  useEffect(() => {
    if (!open) { focusedStaffForOpenRef.current = false; return; }
    if (!prefill?.focusStaff) return;
    if (focusedStaffForOpenRef.current) return;
    focusedStaffForOpenRef.current = true;
    // Defer so the DialogContent + nested sections finish laying out
    // before we measure / scroll. A tiny timeout feels safer than
    // chaining layout-effects across the Dialog portal boundary.
    const t = setTimeout(() => {
      const el = typeof document !== "undefined"
        ? document.getElementById("new-appt-staff-section")
        : null;
      if (el && typeof el.scrollIntoView === "function") {
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch {
          try { el.scrollIntoView(); } catch { /* noop */ }
        }
      }
    }, 180);
    return () => clearTimeout(t);
  }, [open, prefill?.focusStaff]);

  // How many practitioners this service needs (1 by default). Drives the
  // count of Staff dropdowns and validation.
  const requiredStaff = useMemo(() => {
    if (!effectiveServiceId) return 1;
    const s = services.find((x) => x.id === effectiveServiceId);
    return Math.max(1, parseInt(s?.required_staff || 1, 10) || 1);
  }, [effectiveServiceId, services]);

  // Iter-379 · Requires Assistant — a service that keeps required_staff=1
  // but can optionally carry one assistant on the calendar. The staff-side
  // modal exposes ONE optional "Assistant" dropdown; the client-facing
  // booking never sees it. `assistSlots` is the number of extra dropdowns
  // to render (0 or 1 for requires_assistant; required_staff - 1 otherwise).
  const requiresAssistant = useMemo(() => {
    if (!effectiveServiceId) return false;
    const s = services.find((x) => x.id === effectiveServiceId);
    return !!s?.requires_assistant;
  }, [effectiveServiceId, services]);

  const assistSlots = useMemo(() => {
    if (requiresAssistant) return 1;
    return Math.max(0, requiredStaff - 1);
  }, [requiresAssistant, requiredStaff]);

  // Iter-254 — Transportation-mode flag. Fires when the picked service's
  // category name matches "Transportation" (case-insensitive) OR the
  // pre-filled row is already a transportation tile (edit path). Powers
  // every branch below that hides the client/source pickers, disables
  // the staff-training gate, and posts `is_transportation=true`.
  const isTransportationMode = useMemo(() => {
    if (prefill?.is_transportation) return true;
    if (!effectiveServiceId) return false;
    const svc = services.find((x) => x.id === effectiveServiceId);
    return String(svc?.category || "").trim().toLowerCase() === "transportation";
  }, [effectiveServiceId, services, prefill]);

  // Bali-local date string for the fetch. Falls back to today when no slot
  // is picked yet so the operator still sees a useful list.
  const baliDate = useMemo(() => {
    const d = startIso ? new Date(startIso) : new Date();
    return d.toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
  }, [startIso]);

  // Iter-381 · Assistant dropdown roster filter. For Requires Assistant
  // services the Assistant dropdown must ONLY show staff who satisfy
  // BOTH conditions at the same time:
  //   1. Position/Discipline is "Nurse" or "Therapist" (any other
  //      value — Doctor, Driver, Hair Stylist, blank — is excluded).
  //      The `position` field on the staff profile is the single source
  //      of truth; a practitioner missing from this picker means their
  //      profile needs the Position set (data fix, not a code fix).
  //   2. On duty on the active appointment date — the weekday-schedule
  //      `mode` is NOT one of DAY_OFF_MODES. We read the existing
  //      roster field already shipped on every staff doc; no new
  //      roster logic is introduced.
  // Deliberately NOT scoped to the `service_id` training filter so a
  // qualified Nurse/Therapist can still assist on a service they
  // aren't the primary practitioner for. Falls back to the full
  // branch-scoped `staffList` so the roster filter can run even on
  // the service initial-fetch tick when `eligibleStaff` is still null.
  const assistCandidates = useMemo(() => {
    if (!requiresAssistant) return null;
    const wKey = WEEKDAY_KEY[new Date(`${baliDate}T12:00:00`).getDay()];
    const allowed = new Set(["Nurse", "Therapist"]);
    return (staffList || []).filter((s) => {
      if (!s || s.active === false) return false;
      if (!allowed.has(s.position || "")) return false;
      const d = (s.schedule || {})[wKey] || {};
      return !DAY_OFF_MODES.has(d.mode);
    });
  }, [requiresAssistant, staffList, baliDate]);

  // Iter-264 — Specialist Lock Fix (global).
  // Ref-mirror of `locked` so the eligibleStaff / assist auto-pick
  // effects can honour the current lock state WITHOUT adding
  // `locked` to their dependency arrays (which would restart the
  // network fetch every time the operator toggles the padlock).
  const lockedRef = React.useRef(locked);
  React.useEffect(() => { lockedRef.current = locked; }, [locked]);

  // Filtered-staff refresh. Server endpoint already handles the
  // service_ids + on-duty filtering (also merges override schedules). We
  // trigger on service change and date change, and gracefully fall back to
  // the parent's full staffList (unfiltered) on error.
  //
  // Lead-Specialist Rule (SYSTEM_RULES.md): once the operator has picked
  // a primary specialist on step 0 of a multi-service booking (or on a
  // solo booking), that person MUST stay selected on every subsequent
  // step they are trained to perform. We derive `leadStaffId` from the
  // first wizard step (falling back to the current staffId for solo
  // bookings) and prefer it over the eligible list's first row.
  //
  // Specialist-Lock override (iter-264): when the padlock is engaged
  // the current staff MUST be preserved even if the eligible list no
  // longer contains them (e.g. the operator flipped to a date the
  // locked staff is off-duty). The Save flow will surface a clear
  // 409 from the backend if the day/date is genuinely unbookable —
  // silently swapping to another practitioner is a data-integrity
  // regression the user reported repeatedly.
  const leadStaffId = (wizardSteps[0] && wizardSteps[0].staff_id) || staffId || "";
  useEffect(() => {
    if (!open || !branchId || !effectiveServiceId) {
      setEligibleStaff(null);
      return;
    }
    setStaffLoading(true);
    staffApi.get(`/branches/${branchId}/staff`, { params: { service_id: effectiveServiceId, date: baliDate } })
      .then((r) => {
        const wKey = WEEKDAY_KEY[new Date(`${baliDate}T12:00:00`).getDay()];
        const list = (r.data || []).filter((s) => {
          const d = (s.schedule || {})[wKey] || {};
          return !DAY_OFF_MODES.has(d.mode);
        });
        setEligibleStaff(list);
        // Prefer, in order: (a) a LOCKED staff (never auto-swap them),
        // (b) the operator's manual pick if still valid, (c) the
        // session's Lead Specialist if they qualify, (d) fall back to
        // the first eligible row ONLY when nothing above matches.
        setStaffId((current) => {
          if (lockedRef.current && current) return current;
          if (list.find((s) => s.id === current)) return current;
          if (leadStaffId && list.find((s) => s.id === leadStaffId)) return leadStaffId;
          return list[0]?.id || "";
        });
        // Only wipe the assist roster when the primary staff actually
        // changed. Preserves manually-picked assists across a benign
        // eligible-list refetch (e.g. date change that doesn't affect
        // the primary).
        if (!lockedRef.current) {
          setAssistStaffIds([]);
        }
      })
      .catch(() => setEligibleStaff([]))
      .finally(() => setStaffLoading(false));
  }, [open, branchId, effectiveServiceId, baliDate, leadStaffId]);

  // When `requiredStaff` grows, extend assist slots with the next available
  // eligible staff. When it shrinks, trim excess slots.
  // Iter-379 · Requires Assistant services keep required_staff=1 so the
  // auto-fill loop stays at 0 slots — the operator opts in by picking an
  // assistant explicitly in the optional dropdown. We DO trim stale assist
  // ids below when the active service doesn't need them.
  useEffect(() => {
    if (!eligibleStaff) return;
    setAssistStaffIds((current) => {
      // For required_staff > 1 services we still auto-fill. For
      // requires_assistant services (requiredStaff=1, assistSlots=1)
      // we leave the slot blank so the operator makes a conscious pick.
      const autoFillNeed = Math.max(0, requiredStaff - 1);
      const maxKeep = Math.max(autoFillNeed, assistSlots);
      const next = current.slice(0, maxKeep);
      while (next.length < autoFillNeed) {
        const used = new Set([staffId, ...next]);
        const candidate = eligibleStaff.find((s) => !used.has(s.id));
        if (!candidate) break;
        next.push(candidate.id);
      }
      return next;
    });
  }, [requiredStaff, assistSlots, eligibleStaff, staffId]);

  // Whenever the operator changes the Lead Specialist on step 0, forward
  // the pick to every SUBSEQUENT step whose staff_id was still on the
  // previous lead (i.e. never manually customised). Steps a manager has
  // deliberately re-assigned keep their pick untouched. Iter-264:
  // steps flagged `locked: true` are ALSO never overwritten — the
  // padlock is the operator's explicit intent to freeze that provider.
  const prevLeadRef = React.useRef("");
  useEffect(() => {
    if (!isWizard) return;
    if (wizardIndex !== 0) return;
    if (!staffId) return;
    const prevLead = prevLeadRef.current || "";
    if (staffId === prevLead) return;
    setWizardSteps((prev) => prev.map((s, i) => {
      if (i === 0) return s;
      if (s.locked) return s;  // iter-264 — respect the padlock
      // Only propagate to steps that were still tracking the previous lead
      // (or that have no staff set yet). Never override a manual override.
      if (!s.staff_id || s.staff_id === prevLead) {
        return { ...s, staff_id: staffId };
      }
      return s;
    }));
    prevLeadRef.current = staffId;
  }, [staffId, wizardIndex, isWizard]);

  // In wizard mode, hydrate the flat editor state (staffId, roomId,
  // startIso, etc.) from the current step ONLY when the index changes.
  // Uses a ref (hydratedIndexRef, declared above) so this effect doesn't
  // re-fire on every wizardSteps mutation.
  useEffect(() => {
    if (!isWizard) { hydratedIndexRef.current = -1; return; }
    if (hydratedIndexRef.current === wizardIndex) return;
    const step = wizardSteps[wizardIndex];
    if (!step) return;
    hydratedIndexRef.current = wizardIndex;
    setStartIso(step.start_iso || "");
    setStaffId(step.staff_id || "");
    setAssistStaffIds(step.assist_staff_ids || []);
    setRoomId(step.room_id || "");
    setLocked(!!step.locked);
    // Rehydrate the shared Notes textarea from the newly active step so
    // switching / removing steps never carries a previous sibling's notes
    // into the survivor's PATCH payload.
    setNotes(step.notes || "");
    // In multi (ad-hoc) mode, keep the ServiceCombobox in sync with the
    // step being edited so the picker reflects whichever service the
    // step is targeting (or the empty state when the step is fresh).
    if (multiMode) {
      setSelection(step.service_id ? { kind: "service", id: step.service_id } : null);
    }
  }, [wizardIndex, isWizard, wizardSteps, multiMode]);

  // Take the current flat editor state and write it into wizardSteps at the
  // given index so the confirmed rows above render the operator's picks.
  const snapshotCurrentStep = () => {
    setWizardSteps((prev) => {
      const next = prev.slice();
      if (!next[wizardIndex]) return prev;
      next[wizardIndex] = {
        ...next[wizardIndex],
        start_iso: startIso,
        staff_id: staffId,
        assist_staff_ids: assistStaffIds,
        room_id: roomId,
        locked,
        // Persist the shared Notes textarea into the active step so per-step
        // notes stay isolated across step switches.
        notes,
      };
      return next;
    });
  };

  const continueToNextService = () => {
    // Same validation the primary Save runs — fast-fail before advancing.
    if (!effectiveServiceId) { toast.error("Choose a service."); return; }
    if (!staffId) { toast.error("Choose a staff member."); return; }
    if (!startIso) { toast.error("Pick a start time."); return; }
    // Iter-379 · Requires Assistant — the assistant slot is optional,
    // so the strict count check only applies when `required_staff > 1`.
    if (!requiresAssistant) {
      const filled = [staffId, ...assistStaffIds].filter(Boolean);
      if (filled.length !== requiredStaff) {
        toast.error(`This service needs ${requiredStaff} practitioners \u2014 fill every staff slot.`);
        return;
      }
    }
    snapshotCurrentStep();
    // Pre-populate the next step's time to be back-to-back so the operator
    // rarely needs to touch the Time field.
    const dur = wizardSteps[wizardIndex]?.duration_min || 30;
    const nextStart = new Date(new Date(startIso).getTime() + dur * 60000).toISOString();
    setWizardSteps((prev) => {
      const next = prev.slice();
      const nxtIdx = wizardIndex + 1;
      if (next[nxtIdx] && !next[nxtIdx].start_iso) {
        next[nxtIdx] = { ...next[nxtIdx], start_iso: nextStart };
      }
      return next;
    });
    setWizardIndex((i) => i + 1);
  };

  const save = async () => {
    if (!effectiveServiceId) { toast.error("Choose a service (or a package + its included service)."); return; }
    if (eligibleStaff && eligibleStaff.length === 0) {
      toast.error("No trained staff available for this service today.");
      return;
    }
    if (!staffId) { toast.error("Choose a staff member."); return; }
    if (!startIso) { toast.error("Pick a start time."); return; }

    // Multi-practitioner check — must have one distinct trained staff per
    // required_staff position before we hit the backend.
    // Iter-379 · Requires Assistant services keep required_staff=1 but can
    // optionally carry ONE assistant; skip the strict count check and let
    // the dedup / training guards below handle anything the operator
    // actually filled in.
    const filled = [staffId, ...assistStaffIds].filter(Boolean);
    if (!requiresAssistant && filled.length !== requiredStaff) {
      toast.error(`This service needs ${requiredStaff} practitioners \u2014 fill every staff slot.`);
      return;
    }
    if (new Set(filled).size !== filled.length) {
      toast.error("The same staff member is picked twice.");
      return;
    }

    // Client-side guard mirroring the backend enforcement: EVERY assigned
    // staff must be trained in this service (fast fail, no round-trip).
    const src = eligibleStaff || staffList;
    for (const sid of filled) {
      const doc = src.find((s) => s.id === sid);
      if (doc && Array.isArray(doc.service_ids) && !doc.service_ids.includes(effectiveServiceId)) {
        toast.error(`${doc.full_name || "This team member"} is not trained to perform this service.`);
        return;
      }
    }

    // Assemble customer payload. Two paths:
    //  - Existing profile linked: pass customer_id.
    //  - New customer (__new sentinel, or nothing picked but fields filled):
    //    require name + email + whatsapp; backend creates the profile and
    //    stores the branch as their `location` (first-visit branch).
    //
    // Iter-254 · Transportation shortcut: the driver-sign flow captures a
    // manual form only. We POST with `is_transportation=true`, a null
    // customer_id (so nothing lands on a profile / medical history), and a
    // `customer_name` carrying the manual "Full Name" so the calendar tile
    // still shows who is being picked up.
    let customerPayload = {};
    if (isTransportationMode) {
      const fullName = transportName.trim();
      if (!fullName) { toast.error("Enter the guest's full name."); return; }
      if (!transportHotel.trim()) { toast.error("Enter the pick-up hotel / villa."); return; }
      if (!transportAddress.trim()) { toast.error("Enter the full address."); return; }
      if (!transportPhone.trim()) { toast.error("Enter the contact number."); return; }
      customerPayload = {
        customer_name: fullName,
        customer_phone: transportPhone.trim(),
      };
    } else if (client && !client.__new && client.id) {
      customerPayload = {
        customer_id: client.id,
        customer_name: client.full_name || `${client.first_name || ""} ${client.last_name || ""}`.trim(),
        customer_email: client.email || "",
        customer_phone: client.whatsapp || "",
      };
    } else {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      if (!fullName) { toast.error("Enter the client's name."); return; }
      if (!email.trim() && !whatsapp.trim()) {
        toast.error("Please provide either an Email or a WhatsApp number to continue.");
        return;
      }
      customerPayload = {
        customer_name: fullName,
        customer_email: email.trim(),
        customer_phone: whatsapp.trim(),
      };
    }

    setBusy(true);
    // Helper — recognises the backend's staff-time conflict 409s so we can
    // offer the operator a "book anyway (double-book)" confirm. Physical
    // constraints (room full, equipment busy, opening hours, past-time)
    // continue to surface a plain toast — those aren't overridable.
    const isDoubleBookConflict = (err) => {
      const status = err?.response?.status;
      if (status !== 409) return false;
      const detail = String(err?.response?.data?.detail || "").toLowerCase();
      return detail.includes("selected time is not available") || detail.includes("already booked for this time");
    };
    const postAppointment = async (body, dblBook) => {
      const attempt = dblBook ? { ...body, allow_double_book: true } : body;
      return staffApi.post("/appointments", attempt);
    };
    // ---- SOLO EDIT PATH -------------------------------------------------
    // When editing a non-linked appointment (and the operator hasn't added
    // extra services via "+ Add Another Service"), PATCH its existing id
    // rather than POSTing a fresh one (which would create a duplicate tile).
    // Oct 2026 · duplicate-tile fix. A LINKED visit whose wizard
    // collapsed to a single flat service (the operator cleared the
    // package chip and picked one treatment) used to fall through to
    // the create path: it POSTed a brand-new appointment and left the
    // original tile on the calendar — the "substitution made a second
    // tile" bug. With no wizard steps left there is nothing to POST,
    // so always PATCH the clicked appointment in place. `package_id`
    // is deliberately NOT sent, so a package sibling stays part of its
    // bundle and the swap propagates to the order bill.
    if (prefill?.editing && prefill?.id && wizardSteps.length === 0 && deletedStepIds.length === 0) {
      try {
        // Iter-351 — Guest Reassignment on Edit. When the operator has
        // swapped to a different client (or created a brand-new guest)
        // the PATCH body carries the new `customer_id` + `customer_name`
        // so the backend unlinks the appointment from the old guest and
        // re-links it to the new one. `package_id`, `link_group_id`,
        // staff and time all remain intact — only the guest linkage
        // and its denormalised name flip.
        let reassignCustomerId = null;
        let reassignCustomerName = null;
        if (!isTransportationMode) {
          if (client && !client.__new && client.id) {
            if (client.id !== prefill?.customer_id) {
              reassignCustomerId = client.id;
              reassignCustomerName = client.full_name
                || `${client.first_name || ""} ${client.last_name || ""}`.trim()
                || client.email || "";
            }
          } else if (client && client.__new) {
            // Brand-new guest typed during Edit. Create the profile
            // first so we can attach a stable customer_id to the
            // appointment. Rejecting on missing email + WhatsApp
            // matches the primary Create path.
            const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
            if (!fullName) { toast.error("Enter the client's name."); setBusy(false); return; }
            if (!email.trim() && !whatsapp.trim()) {
              toast.error("Please provide either an Email or a WhatsApp number to continue.");
              setBusy(false);
              return;
            }
            try {
              const createResp = await staffApi.post("/customers", {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                email: email.trim(),
                whatsapp: whatsapp.trim(),
                email_credentials: false,
                branch_id: branchId || null,
              });
              const created = createResp.data?.customer;
              if (!created?.id) { throw new Error("customer create returned no id"); }
              reassignCustomerId = created.id;
              reassignCustomerName = created.full_name || fullName;
            } catch (createErr) {
              toast.error(formatApiError(createErr));
              setBusy(false);
              return;
            }
          }
        }
        const body = {
          start_iso: startIso,
          staff_id: staffId,
          // Iter-348 — persist the co-practitioner list on the solo-edit
          // path so adding a 2nd/3rd/4th staff member during Edit
          // spawns every assistant's blocking shadow tile on the
          // calendar live (no manual refresh required). Backend
          // AppointmentUpdate accepts `[]` to clear.
          assist_staff_ids: assistStaffIds,
          room_id: roomId || null,
          duration_min: wizardSteps[wizardIndex]?.duration_min
            || services.find((x) => x.id === effectiveServiceId)?.duration_min
            || prefill.duration_min,
          notes,
          // Iter-264 — persist the Specialist Lock toggle on the
          // solo-edit path so the padlock is not lost when the
          // operator hits Save.
          locked_to_staff: !!locked,
          // Include the picked service so a swap (e.g. Diamond Microdermabrasion
          // → Botox) refreshes the tile's denormalised service_name. Backend
          // treats it as a no-op when unchanged.
          service_id: effectiveServiceId,
        };
        if (reassignCustomerId) {
          body.customer_id = reassignCustomerId;
          body.customer_name = reassignCustomerName;
        }
        // Collapsing a LINKED / PACKAGE visit down to one treatment: the
        // operator cleared the package chip, so the wizard (and with it the
        // other steps' appointment ids) is gone. Those siblings are still
        // booked on the calendar — leaving them behind is what made the save
        // look like "nothing changed, the visit still shows the package".
        // Ask once, then remove them the same way the wizard's own "remove
        // step" does (silent DELETE → status=cancelled, filtered off every
        // calendar view).
        const abandonedSiblings = (Array.isArray(prefill?.linkedSiblings) ? prefill.linkedSiblings : [])
          .filter((s) => s.id && s.id !== prefill.id);
        let dropSiblings = false;
        if (abandonedSiblings.length > 0) {
          const list = abandonedSiblings
            .map((s) => `• ${s.service_name || "treatment"}`)
            .join("\n");
          dropSiblings = typeof window !== "undefined" && window.confirm(
            `This visit also has ${abandonedSiblings.length} other treatment${abandonedSiblings.length === 1 ? "" : "s"} booked:\n\n${list}\n\n`
            + `OK — remove them so the visit is only "${services.find((x) => x.id === effectiveServiceId)?.name || "the selected treatment"}".\n`
            + "Cancel — keep them on the calendar."
          );
        }
        const saveResp = await staffApi.patch(`/appointments/${prefill.id}`, body, { params: { branch_id: branchId } });
        // Write verification — the PATCH returns the appointment AS STORED.
        // Never report success on a treatment change the database didn't
        // actually take (the old "Appointment updated but the tile still
        // shows the original treatment" report).
        const savedAppt = saveResp?.data || null;
        if (body.service_id && savedAppt?.id && savedAppt.service_id !== body.service_id) {
          onCreated && onCreated();
          toast.error(
            `The treatment was NOT saved — this appointment is still "${savedAppt.service_name || "unchanged"}". Please try again, then send this to support if it repeats.`
          );
          return;
        }
        let droppedCount = 0;
        if (dropSiblings) {
          for (const sib of abandonedSiblings) {
            try {
              await staffApi.delete(`/appointments/${sib.id}`, { params: { silent: true } });
              droppedCount += 1;
            } catch (delErr) {
              onCreated && onCreated();
              toast.error(`Couldn't remove "${sib.service_name || "treatment"}": ${formatApiError(delErr)}`);
              return;
            }
          }
        }
        if (droppedCount > 0) {
          toast.success(`Visit updated — now a single treatment (${droppedCount} removed)`);
        } else if (savedAppt?.id && !soloEditChanged(prefill, savedAppt)) {
          // Honest feedback: the operator re-picked the same treatment /
          // time / staff, so the record is byte-identical. Saying
          // "Appointment updated" here is what read as "it says updated but
          // nothing changed".
          toast.success("No changes");
        } else {
          toast.success(reassignCustomerId ? "Guest reassigned" : "Appointment updated");
        }
        onCreated && onCreated();
        onOpenChange(false);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setBusy(false); }
      return;
    }
    // ---- LINKED-VISIT EDIT PATH ----------------------------------------
    // Editing an existing visit: steps WITH an appointment id are PATCHed
    // (dirty fields only), steps WITHOUT an id were added during this edit
    // session and are POSTed as NEW appointments in the same link group, and
    // removed steps are silently DELETEd. This covers delete-then-re-add: the
    // survivor keeps its tile and the added service becomes a second tile.
    //
    // Iter-223 continuity fix — the edit path must ALSO fire when the
    // operator removes EVERY existing step (e.g. dropped the whole
    // package) and adds one or more brand-new services. Without the
    // `deletedStepIds` check we'd fall through to the fresh-POST branch
    // below and create duplicate tiles while the old package siblings
    // are never DELETEd (the removed ids sit in state but no branch
    // acts on them). Any edit session with pending deletes is a
    // linked-visit edit.
    const isEditingLinkedVisit = !!prefill?.editing && (wizardSteps.some((s) => s.id) || deletedStepIds.length > 0);
    if (isEditingLinkedVisit) {
      snapshotCurrentStep();
      const stepsToSave = wizardSteps.map((s, i) => (
        i === wizardIndex
          ? { ...s, start_iso: startIso, staff_id: staffId, assist_staff_ids: assistStaffIds, room_id: roomId, locked, notes }
          : s
      ));
      // Every step must still name a treatment — a persisted step whose
      // service was cleared but never re-picked would otherwise be PATCHed
      // with no `service_id` (falsy fields are skipped in the diff below),
      // reporting "Visit updated" while the tile kept its old treatment.
      for (let i = 0; i < stepsToSave.length; i += 1) {
        const st = stepsToSave[i];
        if (!st.service_id) {
          toast.error(`Step ${i + 1} is missing a service — pick a treatment before saving.`);
          setBusy(false);
          return;
        }
      }
      // Newly-added steps must be fully specified before we write anything.
      for (let i = 0; i < stepsToSave.length; i += 1) {
        const st = stepsToSave[i];
        if (!st.id && (!st.service_id || !st.staff_id || !st.start_iso)) {
          toast.error(`Step ${i + 1}${st.service_name ? ` (${st.service_name})` : ""} is missing a service, staff member or start time.`);
          setBusy(false);
          return;
        }
      }
      const originals = (Array.isArray(prefill?.linkedSiblings) && prefill.linkedSiblings.length > 0)
        ? prefill.linkedSiblings
        : (prefill?.id ? [prefill] : []);
      const origById = Object.fromEntries(originals.map((o) => [o.id, o]));
      // Group id for any added service: reuse the visit's existing
      // link_group_id, else mint one and stamp it on the surviving
      // appointment(s) so the tiles render (and hover-glow) as one visit.
      let linkId = originals.find((o) => o.link_group_id)?.link_group_id || prefill?.link_group_id || null;
      const hasNewSteps = stepsToSave.some((s) => !s.id);
      const mintedLink = hasNewSteps && !linkId;
      if (mintedLink) {
        linkId = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `link-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
      try {
        let touched = 0;
        let added = 0;
        let dblBookAll = false;

        // Iter-221 continuity fix — when the operator removes an item
        // AND adds a new one in the SAME edit session, we PAIR the
        // removals with the additions and reuse the deleted step's
        // appointment id. That preserves the order_id ↔ appointment_id
        // chain (any order.items[].appointment_id reference still
        // resolves) and prevents the "duplicate tile" the user
        // reported on ORD-YZR58A. Extra deletes / extra adds fall
        // back to the original DELETE + POST behaviour.
        const pendingDeletes = [...deletedStepIds];
        const newStepIndices = [];
        for (let i = 0; i < stepsToSave.length; i += 1) {
          if (!stepsToSave[i].id) newStepIndices.push(i);
        }
        while (pendingDeletes.length > 0 && newStepIndices.length > 0) {
          const reusedId = pendingDeletes.shift();
          const swapIdx = newStepIndices.shift();
          stepsToSave[swapIdx] = { ...stepsToSave[swapIdx], id: reusedId, _reusedFromDelete: true };
        }

        for (let i = 0; i < stepsToSave.length; i += 1) {
          const st = stepsToSave[i];
          if (!st.id) {
            // Added during this edit session → POST a brand-new linked tile.
            const body = {
              branch_id: branchId,
              service_id: st.service_id,
              staff_id: st.staff_id,
              assist_staff_ids: st.assist_staff_ids || [],
              locked_to_staff: !!st.locked,
              room_id: st.room_id || null,
              start_iso: st.start_iso,
              notes: typeof st.notes === "string" ? st.notes : "",
              package_id: null,
              link_group_id: linkId,
            };
            if (prefill?.customer_id) body.customer_id = prefill.customer_id;
            else Object.assign(body, customerPayload);
            try {
              await postAppointment(body, dblBookAll);
            } catch (err) {
              if (!isDoubleBookConflict(err) || dblBookAll) throw err;
              const ok = typeof window !== "undefined" && window.confirm(
                "This team member is already booked at this time.\n\nBook anyway (double-book)?"
              );
              if (!ok) throw err;
              dblBookAll = true;
              await postAppointment(body, true);
            }
            added += 1;
            continue;
          }
          const orig = origById[st.id] || {};
          const patch = {};
          if (st._reusedFromDelete) {
            // The operator removed one row and added another in the
            // same session — we're re-purposing this appointment id
            // instead of deleting it. Send the FULL new state
            // (service, staff, time, duration, notes) as a single
            // PATCH so the tile visibly swaps to the new service on
            // the calendar and the linked order line still points at
            // the same appointment_id.
            if (st.service_id) patch.service_id = st.service_id;
            if (st.staff_id) patch.staff_id = st.staff_id;
            if (st.start_iso) patch.start_iso = st.start_iso;
            if (st.duration_min) patch.duration_min = st.duration_min;
            patch.room_id = st.room_id || null;
            patch.notes = typeof st.notes === "string" ? st.notes : "";
            if (mintedLink) patch.link_group_id = linkId;
            // Iter-223 — when the reused id was previously a package
            // redemption but the operator swapped it for a solo
            // service (e.g. removed the package + added a Botox),
            // strip its package_id so reports stop counting it as a
            // package redemption for a service it no longer holds.
            // Empty string is the "clear" sentinel on the backend.
            if (orig.package_id && !st.package_id) patch.package_id = "";
            await staffApi.patch(`/appointments/${st.id}`, patch, { params: { branch_id: branchId } });
            touched += 1;
            continue;
          }
          if (st.start_iso && st.start_iso !== orig.start_iso) patch.start_iso = st.start_iso;
          if (st.staff_id && st.staff_id !== orig.staff_id) patch.staff_id = st.staff_id;
          // Iter-371 · Multi-staff assist edit fix. The linked-visit
          // edit path previously omitted `assist_staff_ids` from the
          // diff, so setting "Staff 2 = Therapist Made" on a package
          // step (e.g. IV Triple Booster) never reached the backend —
          // the tile stayed single-staff and the assistant's column
          // never spawned its blocking shadow. Ship the field whenever
          // the sorted, cleaned list differs from the original. `[]`
          // is the explicit-clear sentinel per AppointmentUpdate.
          const nextAssist = Array.isArray(st.assist_staff_ids)
            ? st.assist_staff_ids.filter((sid) => sid && sid !== st.staff_id)
            : [];
          const origAssist = Array.isArray(orig.assist_staff_ids)
            ? orig.assist_staff_ids.filter((sid) => sid && sid !== orig.staff_id)
            : [];
          const sameAssist = nextAssist.length === origAssist.length
            && [...nextAssist].sort().every((sid, i) => sid === [...origAssist].sort()[i]);
          if (!sameAssist) patch.assist_staff_ids = nextAssist;
          if ((st.room_id || null) !== (orig.room_id || null)) patch.room_id = st.room_id || null;
          if (st.duration_min && st.duration_min !== orig.duration_min) patch.duration_min = st.duration_min;
          // Service swap on a linked step (e.g. ad-hoc row where the operator
          // switched the treatment). Send it through so the tile's
          // service_name updates alongside staff/time — otherwise the row
          // still renders the original service on the calendar.
          if (st.service_id && st.service_id !== orig.service_id) patch.service_id = st.service_id;
          // Each step owns its own notes now. Compare the per-step notes
          // captured at snapshot time against that step's own original —
          // this guarantees switching / removing steps can't leak one
          // sibling's notes into another's PATCH payload.
          const stepNotes = typeof st.notes === "string" ? st.notes : "";
          if (stepNotes !== (orig.notes || "")) patch.notes = stepNotes;
          // Specialist Lock — the padlock is editable on every step of the
          // Edit wizard but was never diffed here, so flipping it on a
          // linked / package visit silently did nothing on save.
          if (!!st.locked !== !!orig.locked_to_staff) patch.locked_to_staff = !!st.locked;
          // A freshly-minted group id must be stamped on the survivor so it
          // links up with the appointments POSTed above.
          if (mintedLink) patch.link_group_id = linkId;
          if (Object.keys(patch).length === 0) continue;
          const stepResp = await staffApi.patch(`/appointments/${st.id}`, patch, { params: { branch_id: branchId } });
          // Write verification (see the solo path) — a treatment swap that
          // didn't land must surface as an error, never as "Visit updated".
          const savedStep = stepResp?.data || null;
          if (patch.service_id && savedStep?.id && savedStep.service_id !== patch.service_id) {
            onCreated && onCreated();
            toast.error(
              `Step ${i + 1}: the treatment was NOT saved — it is still "${savedStep.service_name || "unchanged"}". Please try again, then send this to support if it repeats.`
            );
            return;
          }
          touched += 1;
        }
        // Silently DELETE any UNPAIRED steps the operator removed
        // from the wizard during this edit (paired-with-adds ones
        // were re-purposed via PATCH above so their appointment ids
        // survive). `?silent=true` (staff-only) skips the package
        // cascade + customer cancellation email — appropriate here
        // because the operator is trimming the visit itinerary, not
        // cancelling the whole visit. The DELETE marks
        // status=cancelled so the tile is filtered out of every
        // calendar view.
        let deleted = 0;
        const deleteErrors = [];
        for (const delId of pendingDeletes) {
          try {
            await staffApi.delete(`/appointments/${delId}`, { params: { silent: true } });
            deleted += 1;
          } catch (e) {
            deleteErrors.push(e);
          }
        }
        if (deleteErrors.length > 0) {
          // Refresh the calendar so PATCH updates + partial deletes render,
          // then surface the failure and KEEP the modal open so the
          // operator can retry the remaining removals. Reporting success
          // here would mask a partial write.
          onCreated && onCreated();
          toast.error(`Couldn't remove ${deleteErrors.length} service${deleteErrors.length === 1 ? "" : "s"}: ${formatApiError(deleteErrors[0])}`);
          return;
        }
        const changes = touched + added + deleted;
        const parts = [];
        if (touched > 0) parts.push(`${touched} update${touched === 1 ? "" : "s"}`);
        if (added > 0) parts.push(`${added} added`);
        if (deleted > 0) parts.push(`${deleted} removed`);
        toast.success(changes > 0 ? `Visit updated (${parts.join(", ")})` : "No changes");
        onCreated && onCreated();
        onOpenChange(false);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setBusy(false); }
      return;
    }
    // Helper — recognises the backend's staff-time conflict 409s so we can
    // offer the operator a "book anyway (double-book)" confirm. Physical
    // constraints (room full, equipment busy, opening hours, past-time)
    // continue to surface a plain toast — those aren't overridable.
    // Best-effort rollback for the package flow — if step K fails after
    // steps 0..K-1 have already been persisted, silently delete them so
    // the calendar isn't polluted with orphan "part-of-a-package" tiles
    // (which is exactly what caused the "single service showing as part
    // of a package" bug — those were leftover step-0s from earlier
    // failed wizard runs).
    const rollbackCreated = async (ids) => {
      if (!ids.length) return;
      await Promise.all(ids.map((id) => staffApi.delete(`/appointments/${id}`, { params: { silent: true } }).catch(() => null)));
    };
    try {
      if (isWizard) {
        // Package flow: snapshot the current (last) step, then POST all
        // constituent appointments in order with a shared `link_group_id`
        // so they render as a linked visit on the calendar. Only the first
        // POST carries new-customer fields — subsequent POSTs reference the
        // returned customer_id so we don't duplicate the profile.
        snapshotCurrentStep();
        const stepsToSave = wizardSteps.map((s, i) => (
          i === wizardIndex
            ? { ...s, start_iso: startIso, staff_id: staffId, assist_staff_ids: assistStaffIds, room_id: roomId, locked, notes }
            : s
        ));
        // Client-side pre-validation: EVERY step must have a staff_id and
        // start_iso before we start POSTing. Prevents the orphan-step-0
        // bug where clicking Save on step 0 would create step 0 with
        // dr.Angel, then blow up on step 1 with an empty staff_id, leaving
        // step 0 dangling in the DB tagged with the package's link_group_id.
        for (let i = 0; i < stepsToSave.length; i += 1) {
          const st = stepsToSave[i];
          if (!st.staff_id || !st.start_iso) {
            toast.error(`Step ${i + 1} (${st.service_name}) is missing a staff member or start time.`);
            setBusy(false);
            return;
          }
        }
        const linkId = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `link-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        let carriedCustomerId = customerPayload.customer_id || null;
        // Once the operator agrees to double-book one step, keep the flag
        // on for the remaining steps of the same package so we don't have
        // to re-confirm for every single service in the visit.
        let dblBookAll = false;
        // Track ids of the created appointments so a mid-loop failure can
        // roll them back atomically.
        const createdIds = [];
        for (let i = 0; i < stepsToSave.length; i += 1) {
          const st = stepsToSave[i];
          const body = {
            branch_id: branchId,
            service_id: st.service_id,
            staff_id: st.staff_id,
            assist_staff_ids: st.assist_staff_ids || [],
            locked_to_staff: !!st.locked,
            room_id: st.room_id || null,
            start_iso: st.start_iso,
            // Each step owns its own notes so multi-service create writes
            // the correct per-service note rather than duplicating the
            // last-active textarea value onto step 0.
            notes: typeof st.notes === "string" ? st.notes : "",
            // package_id stays null for ad-hoc "+ Add Another Service"
            // bookings — those are grouped by link_group_id (so the tiles
            // hover-glow together) but don't consume any package.
            package_id: multiMode ? null : selection.id,
            link_group_id: linkId,
            source_customer_id: i === 0 ? (source?.id || null) : null,
          };
          if (carriedCustomerId) {
            body.customer_id = carriedCustomerId;
          } else {
            Object.assign(body, customerPayload);
          }
          let resp;
          try {
            resp = await postAppointment(body, dblBookAll);
          } catch (err) {
            if (isDoubleBookConflict(err) && !dblBookAll) {
              const ok = typeof window !== "undefined" && window.confirm(
                "This team member is already booked at this time.\n\nBook anyway (double-book)?"
              );
              if (!ok) {
                await rollbackCreated(createdIds);
                throw err;
              }
              dblBookAll = true;
              try {
                resp = await postAppointment(body, true);
              } catch (retryErr) {
                await rollbackCreated(createdIds);
                throw retryErr;
              }
            } else {
              await rollbackCreated(createdIds);
              throw err;
            }
          }
          createdIds.push(resp.data?.id);
          // Once the first POST succeeds we have a customer_id (either the
          // linked existing one OR the freshly auto-created guest). Reuse
          // it for every following step so we don't spawn duplicate
          // profiles.
          carriedCustomerId = carriedCustomerId || resp.data?.customer_id || null;
        }
        // All siblings persisted — dispatch ONE consolidated confirmation
        // email listing every service in the visit. Skipped on the
        // per-POST path (link_group_id branch) inside the backend so the
        // customer never gets a partial itinerary.
        try {
          await staffApi.post(`/appointments/link-group/${linkId}/notify-booked`);
        } catch { /* silent — non-blocking notification */ }
        toast.success(`${multiMode ? "Services" : "Package"} booked (${stepsToSave.length} appointments).`);
      } else {
        const body = {
          branch_id: branchId,
          service_id: effectiveServiceId,
          staff_id: staffId,
          assist_staff_ids: assistStaffIds,
          locked_to_staff: locked,
          room_id: roomId || null,
          start_iso: startIso,
          notes,
          package_id: null,
          source_customer_id: isTransportationMode ? null : (source?.id || null),
          ...customerPayload,
        };
        if (isTransportationMode) {
          body.is_transportation = true;
          body.transport_meta = {
            full_name: transportName.trim(),
            hotel: transportHotel.trim(),
            address: transportAddress.trim(),
            phone: transportPhone.trim(),
            // Treatment time mirrors the appointment time so the driver
            // sign renders "Treatment at HH.MM PM" without needing a
            // second time picker.
            treatment_time: (timePieces.timeLabel && timePieces.timeLabel !== "—") ? timePieces.timeLabel : "",
          };
        }
        try {
          await postAppointment(body, false);
        } catch (err) {
          if (!isDoubleBookConflict(err)) throw err;
          const ok = typeof window !== "undefined" && window.confirm(
            "This team member is already booked at this time.\n\nBook anyway (double-book)?"
          );
          if (!ok) throw err;
          await postAppointment(body, true);
        }
        toast.success(isTransportationMode ? "Transportation booked" : "Appointment created");
      }
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // The name-entry fields only render when we're NOT linked to an existing
  // customer. `client === null` means fresh entry; `client.__new` means the
  // operator explicitly clicked "Create new customer" from the picker.
  const showNewFields = !client || client.__new;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[86vh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="new-appt-modal"
        style={branchThemeStyle("branch", branch?.name)}
      >
        <DialogHeader className="px-4 py-2.5 border-b border-primary/40 bg-primary text-primary-foreground rounded-t-lg">
          <DialogTitle className="text-sm font-semibold text-primary-foreground">
            {prefill?.editing ? "Edit visit" : "New Appointment"}
          </DialogTitle>
          <DialogDescription className="sr-only">Book a service for this location.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1.5 text-sm">
          {/* ── Service ── */}
          <div>
            {!multiMode && (
              <>
                <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Service</Label>
                <div className="mt-1">
                  <ServiceCombobox
                    value={selection}
                    onChange={(v) => {
                      setSelection(v);
                      if (v?.kind !== "package") setPackageServiceId("");
                    }}
                    services={services}
                    packages={packages}
                    branch={branch}
                    branchId={branchId}
                    testId="new-appt-service"
                  />
                </div>
                {/* "+ Add Another Service" — visible in single-service mode once
                    a service is picked (not for packages, which already run
                    a wizard). Lets the operator chain 2..N different services
                    in one visit under a shared link_group_id, mirroring the
                    package hover-glow behaviour without stamping a
                    package_id. */}
                {!isWizard && selection?.kind === "service" && effectiveServiceId && (
                  <button
                    type="button"
                    onClick={() => {
                      // Snapshot the current form state as step 0, then push
                      // an empty step 1 for the second service. Auto-advance
                      // to step 1 so the time/staff pane targets the new
                      // step; the inline picker inside step 1's row lets
                      // the operator search & pick right there.
                      const svc = services.find((x) => x.id === effectiveServiceId);
                      // Total block (service + processing) so the next
                      // ad-hoc "+ Add Another Service" step starts
                      // strictly after the previous service's zebra
                      // buffer clears the specialist.
                      const _svcTime0 = svc?.service_time_min || svc?.duration_min || 30;
                      const _procTime0 = Math.max(0, svc?.processing_time_min || 0);
                      const _totalDur0 = _svcTime0 + _procTime0;
                      const step0 = {
                        // Editing an existing solo appointment → keep its id
                        // so Save PATCHes it in place while the added service
                        // POSTs as a NEW linked tile (instead of the edit
                        // overwriting this appointment with the new service).
                        ...(prefill?.editing && prefill?.id ? { id: prefill.id } : {}),
                        service_id: effectiveServiceId,
                        service_name: svc?.name || "",
                        duration_min: _totalDur0,
                        start_iso: startIso,
                        staff_id: staffId,
                        assist_staff_ids: assistStaffIds,
                        room_id: roomId,
                        locked,
                        notes,
                      };
                      const nextStart = startIso
                        ? new Date(new Date(startIso).getTime() + ((step0.duration_min || 30) * 60000)).toISOString()
                        : "";
                      const step1 = {
                        service_id: "",
                        service_name: "",
                        duration_min: 30,
                        start_iso: nextStart,
                        staff_id: "",
                        assist_staff_ids: [],
                        room_id: "",
                        locked: false,
                        notes: "",
                      };
                      setWizardSteps([step0, step1]);
                      setWizardIndex(1);
                      setMultiMode(true);
                      setSelection(null);
                      setStartIso(nextStart);
                      // Freshly-added step 1 starts with empty notes; clear
                      // the shared textarea so operator input for step 1
                      // isn't seeded with step 0's notes.
                      setNotes("");
                    }}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                    data-testid="new-appt-add-another-service"
                  >
                    <span className="inline-block w-4 h-4 rounded-full bg-primary text-primary-foreground text-[11px] leading-4 text-center">+</span>
                    Add Another Service
                  </button>
                )}
              </>
            )}
            {multiMode && (
              <>
                <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Services</Label>
                <div className="mt-1 space-y-1.5" data-testid="new-appt-multi-wizard">
                  {/* Ad-hoc queue — each step embeds its own ServiceCombobox
                      so the operator can search and pick a service directly
                      in the row (matches the "pick the first service" UX). */}
                  {wizardSteps.map((st, i) => {
                    const activate = () => { if (i !== wizardIndex) { snapshotCurrentStep(); setWizardIndex(i); } };
                    return (
                      <div
                        key={i}
                        onClick={activate}
                        className={
                          "rounded-md border px-3 py-2 " +
                          (i === wizardIndex ? "border-primary/40 bg-primary/[0.03]" : "border-border bg-muted/30")
                        }
                        data-testid={`multi-step-${i}`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Step {i + 1}</div>
                          {wizardSteps.length > 1 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const removed = wizardSteps[i];
                                // Snapshot the currently active flat editor
                                // state into wizardSteps FIRST so removing
                                // an inactive row can't discard the
                                // operator's unsaved edits on the active
                                // row (via the hydration reset below).
                                if (i !== wizardIndex) {
                                  snapshotCurrentStep();
                                }
                                // Persisted step (has an appointment id) →
                                // queue a silent DELETE on save so the
                                // calendar tile actually disappears. Purely
                                // in-memory steps just vanish from state.
                                if (removed?.id) {
                                  setDeletedStepIds((cur) => (cur.includes(removed.id) ? cur : [...cur, removed.id]));
                                }
                                setWizardSteps((prev) => {
                                  const next = prev.filter((_, idx) => idx !== i);
                                  // Only creation flows collapse back to
                                  // the flat single-service layout when a
                                  // step is removed. Edit sessions must
                                  // stay in multi-mode so the linked-visit
                                  // save path (PATCH + DELETE) fires.
                                  if (next.length <= 1 && !prefill?.editing) {
                                    if (next[0]) {
                                      const only = next[0];
                                      setSelection(only.service_id ? { kind: "service", id: only.service_id } : null);
                                      setStartIso(only.start_iso || "");
                                      setStaffId(only.staff_id || "");
                                      setAssistStaffIds(only.assist_staff_ids || []);
                                      setRoomId(only.room_id || "");
                                      setLocked(!!only.locked);
                                    }
                                    setMultiMode(false);
                                    setWizardIndex(0);
                                    return [];
                                  }
                                  return next;
                                });
                                // Adjust the active index relative to the
                                // removed row, and force the hydration
                                // effect to reload the new active step's
                                // flat state (staff/time/room) even when
                                // the numeric index doesn't change.
                                setWizardIndex((cur) => {
                                  if (cur === i) return Math.max(0, i - 1);
                                  if (cur > i) return cur - 1;
                                  return cur;
                                });
                                hydratedIndexRef.current = -1;
                              }}
                              className="text-muted-foreground hover:text-destructive text-[11px]"
                              data-testid={`multi-step-${i}-remove`}
                            >Remove</button>
                          )}
                        </div>
                        <ServiceCombobox
                          value={st.service_id ? { kind: "service", id: st.service_id } : null}
                          onChange={(v) => {
                            activate();
                            setWizardSteps((prev) => {
                              const next = prev.slice();
                              if (!next[i]) return prev;
                              if (!v || v.kind !== "service") {
                                next[i] = { ...next[i], service_id: "", service_name: "", duration_min: 30 };
                              } else {
                                const svc = services.find((x) => x.id === v.id);
                                // Total block for cursor + tile height so
                                // the zebra section is reserved on the
                                // calendar just like the service portion.
                                const _svcTime = svc?.service_time_min || svc?.duration_min || 30;
                                const _procTime = Math.max(0, svc?.processing_time_min || 0);
                                next[i] = {
                                  ...next[i],
                                  service_id: v.id,
                                  service_name: svc?.name || "",
                                  duration_min: _svcTime + _procTime,
                                };
                              }
                              return next;
                            });
                          }}
                          services={services}
                          packages={[]}
                          branch={branch}
                          branchId={branchId}
                          testId={`multi-step-${i}-service`}
                        />
                        {/* Confirmed staff/time/room summary — shown only on
                            inactive steps that already have staff picked
                            (i.e. snapshotted from a previous edit). The
                            active step's staff/time/room live in the shared
                            Date/Time/Staff pane below, so we skip it here. */}
                        {i !== wizardIndex && st.staff_id && st.start_iso && (() => {
                          const staffDoc = staffList.find((s) => s.id === st.staff_id);
                          const roomDoc = st.room_id ? rooms.find((r) => r.id === st.room_id) : null;
                          const startLabel = new Date(st.start_iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar" });
                          const endLabel = new Date(new Date(st.start_iso).getTime() + (st.duration_min * 60000)).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar" });
                          const parts = [`${startLabel}–${endLabel}`];
                          if (staffDoc?.full_name) parts.push(staffDoc.full_name);
                          if (roomDoc?.name) parts.push(roomDoc.name);
                          return (
                            <div
                              className="mt-1.5 text-[11px] text-muted-foreground truncate"
                              data-testid={`multi-step-${i}-summary`}
                            >
                              {parts.join(" · ")}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => {
                      snapshotCurrentStep();
                      const nextStart = startIso
                        ? new Date(new Date(startIso).getTime() + ((wizardSteps[wizardIndex]?.duration_min || 30) * 60000)).toISOString()
                        : "";
                      setWizardSteps((prev) => [...prev, {
                        service_id: "", service_name: "", duration_min: 30,
                        start_iso: nextStart, staff_id: "", assist_staff_ids: [],
                        room_id: "", locked: false, notes: "",
                      }]);
                      setWizardIndex(wizardSteps.length); // point at the new blank step
                      setStartIso(nextStart);
                      // New step's notes start empty — clear the shared
                      // textarea so operator's input targets step N only.
                      setNotes("");
                    }}
                    className="w-full text-xs text-primary hover:underline text-left px-3 py-1"
                    data-testid="new-appt-add-another-service-more"
                  >+ Add Another Service</button>
                </div>
              </>
            )}
            {!multiMode && selection?.kind === "package" && (
              <div className="mt-3 space-y-1.5" data-testid="new-appt-package-wizard">
                {/* Compact step list — booked steps at top, then the active
                    step (rendered by the shared controls below), then the
                    upcoming steps as collapsed rows. Time / staff / room
                    controls for the current step render below via the
                    existing single-service layout. */}
                {wizardSteps.map((st, i) => {
                  if (i === wizardIndex) {
                    const svc = services.find((s) => s.id === st.service_id);
                    // Iter-339 — Substitution dropdown. When the active
                    // step's package item declares alternates in the
                    // Brand Catalog, render a Select next to the service
                    // name so the operator can pick the original OR any
                    // substitute WITHOUT breaking the bundle: the POST
                    // still carries the package's `package_id`, so
                    // PackageEngine's Stewart Gallacher emit keeps the
                    // invoice as ONE bundled item at the package price.
                    const subOptions = substitutionOptionsForItem(activePackage, i, services);
                    const hasSubs = Array.isArray(subOptions) && subOptions.length > 1;
                    return (
                      <div key={i} className="rounded-md border border-primary/30 bg-primary/[0.03] px-3 py-2" data-testid={`wizard-step-active-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] uppercase tracking-wider text-primary font-medium">Step {i + 1} of {wizardSteps.length}</span>
                          <span className="text-[11px] text-muted-foreground">{svc?.duration_min || st.duration_min} min</span>
                        </div>
                        {/* Oct 2026 · substitute IN PLACE. Every package
                            step now carries a searchable service picker —
                            the catalog's declared substitutes show as
                            quick-pick chips, any other branch treatment is
                            one search away. Swapping here keeps the SAME
                            appointment (PATCH), so the tile, the package
                            bundle and the POS bill all follow without a
                            cancel + re-book (which created duplicate
                            tiles and billed the substitute as an extra). */}
                        <div className="mt-1 space-y-1.5">
                          <ServiceCombobox
                            value={st.service_id ? { kind: "service", id: st.service_id } : null}
                            onChange={(v) => {
                              if (v?.kind === "service") swapStepService(i, v.id);
                              else if (!v) clearStepService(i);
                            }}
                            services={services}
                            packages={[]}
                            branch={branch}
                            branchId={branchId}
                            testId={`wizard-step-${i}-substitute`}
                          />
                          {hasSubs && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Included / substitutes</span>
                              {subOptions.map((opt) => {
                                const optDur = (opt.service_time_min || opt.duration_min || 30) + Math.max(0, opt.processing_time_min || 0);
                                const picked = st.service_id === opt.id;
                                return (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => swapStepService(i, opt.id)}
                                    data-testid={`wizard-step-${i}-substitute-opt-${opt.id}`}
                                    className={
                                      "rounded-full border px-2 py-0.5 text-[11px] transition-colors " +
                                      (picked
                                        ? "border-primary bg-primary/10 text-primary"
                                        : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground")
                                    }
                                  >
                                    {opt.name} · {optDur}m
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  const done = i < wizardIndex;
                  const staffDoc = done ? staffList.find((s) => s.id === st.staff_id) : null;
                  const roomDoc = done ? rooms.find((r) => r.id === st.room_id) : null;
                  const timeLabel = done && st.start_iso
                    ? `${new Date(st.start_iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar" })} – ${new Date(new Date(st.start_iso).getTime() + (st.duration_min * 60000)).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar" })}`
                    : null;
                  // Only DONE rows (steps behind the active one) can be jumped
                  // back to. Pending rows stay locked so the operator can't
                  // skip ahead — they must use "Continue to next service".
                  // Snapshotting the current step before switching preserves
                  // any unsaved edits the operator made in the shared pane.
                  const jumpBack = () => {
                    if (!done) return;
                    snapshotCurrentStep();
                    hydratedIndexRef.current = -1;
                    setWizardIndex(i);
                  };
                  return (
                    <div
                      key={i}
                      onClick={jumpBack}
                      role={done ? "button" : undefined}
                      tabIndex={done ? 0 : undefined}
                      onKeyDown={(e) => { if (done && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); jumpBack(); } }}
                      className={
                        "rounded-md border px-3 py-1.5 flex items-start gap-2 text-xs transition-colors " +
                        (done
                          ? "border-border bg-muted/30 cursor-pointer hover:border-primary/40 hover:bg-primary/[0.04]"
                          : "border-dashed border-border/60 bg-background")
                      }
                      data-testid={done ? `wizard-step-done-${i}` : `wizard-step-pending-${i}`}
                    >
                      <div className="w-10 flex-shrink-0 text-muted-foreground">
                        {done && timeLabel ? <span className="text-[10px]">{timeLabel}</span> : <span className="text-[10px]">–</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{st.service_name} <span className="text-muted-foreground">({st.duration_min} min)</span></div>
                        {done && (staffDoc || roomDoc) && (
                          <div className="text-[10px] text-muted-foreground truncate">{staffDoc?.full_name || ""}{staffDoc && roomDoc ? " · " : ""}{roomDoc?.name || ""}</div>
                        )}
                      </div>
                      {done && <span className="text-[10px] text-primary/70 self-center ml-1">Edit</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── When + Who · Date / Time / Room on one equal-thirds row ── */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Date</Label>
              <div className="mt-1 h-9 rounded-md border border-input bg-muted/30 px-3 flex items-center text-xs" data-testid="new-appt-date">
                {timePieces.dateLabel}
              </div>
            </div>
            <div>
              <Label htmlFor="new-appt-time" className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Time</Label>
              <Input
                id="new-appt-time"
                type="time"
                value={timePieces.timeLabel === "—" ? "" : timePieces.timeLabel}
                onChange={(e) => onTimeChange(e.target.value)}
                data-testid="new-appt-time"
                className="mt-1 h-9"
              />
            </div>
            <div>
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Room</Label>
              <Select value={roomId} onValueChange={setRoomId}>
                <SelectTrigger data-testid="new-appt-room" className="mt-1 h-9"><SelectValue placeholder="Auto" /></SelectTrigger>
                <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-3" id="new-appt-staff-section" data-testid="new-appt-staff-section">
              {/* Primary staff — filtered to only practitioners trained for
                  this service (server-side via /branches/{bid}/staff?service_id).
                  Lock toggle sits next to the select so the operator can
                  flag "customer requested this specific staff" at booking
                  time. Iter-380 · The ID anchor + testid are used by the
                  Missing-Assistant alert to scroll the dialog here when
                  the operator opens Edit via the amber banner. */}
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Staff</Label>
                {staffLoading && <span className="text-[10px] text-muted-foreground">Loading…</span>}
              </div>
              {eligibleStaff && eligibleStaff.length === 0 && !locked ? (
                <div
                  className="h-9 rounded-md border border-destructive/40 bg-destructive/5 px-3 flex items-center text-xs text-destructive"
                  data-testid="new-appt-no-staff-warning"
                >
                  No trained staff available for this service today.
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={staffId} onValueChange={setStaffId} disabled={locked}>
                    <SelectTrigger data-testid="new-appt-staff" className="h-9 flex-1" data-locked={locked ? "true" : undefined}><SelectValue placeholder="Choose staff" /></SelectTrigger>
                    <SelectContent>
                      {/* Iter-264 — if the current pick is locked but
                          not in the eligible list (e.g. the day flipped
                          to their day-off), include them anyway so the
                          Select still renders their name instead of an
                          empty label. The backend will still 409 if the
                          time is genuinely unbookable — the operator
                          must resolve it explicitly rather than the UI
                          swapping them silently. */}
                      {(() => {
                        const base = (eligibleStaff || staffList);
                        const known = new Set(base.map((s) => s.id));
                        const rows = [...base];
                        if (locked && staffId && !known.has(staffId)) {
                          const extra = staffList.find((s) => s.id === staffId);
                          if (extra) rows.unshift(extra);
                        }
                        return rows.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => setLocked((v) => !v)}
                    className={
                      "h-9 w-9 rounded-md border flex items-center justify-center transition-colors " +
                      (locked
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-input bg-background text-muted-foreground hover:text-foreground")
                    }
                    title={locked ? "Customer requested this staff — unlock to allow reassignment" : "Lock to this staff (customer request)"}
                    data-testid="new-appt-lock"
                    aria-pressed={locked ? "true" : "false"}
                  >
                    {locked ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
                  </button>
                </div>
              )}
              {/* Extra practitioners — one dropdown per required_staff > 1,
                  OR one optional dropdown for Requires Assistant services.
                  Iter-381 · For Requires Assistant services the dropdown
                  source is `assistCandidates` (branch staff where
                  position ∈ {Nurse, Therapist} AND on-duty on the active
                  date). For required_staff > 1 services we keep the
                  trained `eligibleStaff` list unchanged. Already-picked
                  ids are hidden either way so the operator can't pick
                  the same person twice. */}
              {assistSlots > 0 && (
                <div className="mt-2 space-y-2">
                  {Array.from({ length: assistSlots }).map((_, idx) => {
                    const assistantMode = requiresAssistant;
                    const sourceList = assistantMode
                      ? (assistCandidates || [])
                      : (eligibleStaff || []);
                    // The dropdown is still rendered (not hidden) for
                    // the assistant path even when `sourceList` is
                    // empty, so the operator can see the empty-state
                    // "No assistant available for this date" message
                    // instead of a silently missing field. For the
                    // multi-staff path we keep the old behaviour of
                    // hiding the row until the eligible list resolves.
                    if (!assistantMode && (!eligibleStaff || eligibleStaff.length === 0)) {
                      return null;
                    }
                    const used = new Set([staffId, ...assistStaffIds.filter((_, i) => i !== idx)]);
                    const options = sourceList.filter((s) => !used.has(s.id));
                    const val = assistStaffIds[idx] || "";
                    const label = assistantMode ? "Assistant" : `Staff ${idx + 2}`;
                    const emptyMsg = assistantMode
                      ? "No assistant available for this date"
                      : "No other trained staff available.";
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground w-20">
                          <UserPlus className="w-3 h-3" /> {label}
                        </div>
                        <Select
                          value={val}
                          onValueChange={(v) => setAssistStaffIds((cur) => {
                            const next = cur.slice();
                            next[idx] = v;
                            return next;
                          })}
                        >
                          <SelectTrigger data-testid={`new-appt-assist-staff-${idx}`} className="h-9 flex-1">
                            <SelectValue placeholder={assistantMode ? "Choose assistant (optional)" : "Choose staff"} />
                          </SelectTrigger>
                          <SelectContent>
                            {options.length === 0 ? (
                              <div
                                className="px-3 py-2 text-xs text-muted-foreground"
                                data-testid={assistantMode ? `new-appt-assistant-empty-${idx}` : undefined}
                              >
                                {emptyMsg}
                              </div>
                            ) : options.map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {assistantMode && val && (
                          <button
                            type="button"
                            onClick={() => setAssistStaffIds((cur) => {
                              const next = cur.slice();
                              next[idx] = "";
                              return next;
                            })}
                            className="text-[11px] text-muted-foreground hover:text-destructive underline underline-offset-2"
                            data-testid={`new-appt-assist-clear-${idx}`}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Client ── */}
          {!isTransportationMode && (
          <div className="pt-2 border-t border-border">
            <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Users className="w-3 h-3" /> Client
            </Label>
            <div className="mt-1">
              <CustomerCombobox
                value={client && !client.__new ? client : null}
                onPick={(c) => {
                  if (c.__new) {
                    // Treat the typed string as first name (or "First Last").
                    const parts = c.full_name.trim().split(/\s+/);
                    setFirstName(parts[0] || "");
                    setLastName(parts.slice(1).join(" ") || "");
                    setClient({ __new: true, full_name: c.full_name });
                    setSource(null);
                    setSourceLocked(false);
                  } else {
                    setClient(c);
                    setFirstName(c.first_name || (c.full_name || "").split(" ")[0] || "");
                    setLastName(c.last_name || (c.full_name || "").split(" ").slice(1).join(" ") || "");
                    setEmail(c.email || "");
                    setWhatsapp(c.whatsapp || "");
                    hydrateSourceFromCustomer(c);
                  }
                }}
                onClear={() => { setClient(null); setSource(null); setSourceLocked(false); }}
                allowCreate
                testId="new-appt-client"
              />
            </div>

            {showNewFields && (
              <div className="mt-2 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="new-appt-first" className="h-9" placeholder="First name" />
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="new-appt-last" className="h-9" placeholder="Last name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-appt-email" className="h-9" placeholder="Email (optional)" />
                  <PhoneInput value={whatsapp} onChange={setWhatsapp} testId="new-appt-phone" placeholder="WhatsApp / phone (optional)" />
                </div>
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Info className="w-3 h-3 flex-shrink-0" />
                  Provide at least one — email or WhatsApp. Login username = email (or WhatsApp if no email); password = first 5 chars of the identifier.
                </p>
              </div>
            )}
          </div>
          )}

          {/* ── Iter-254 · Transportation manual form ────────────────────
              Renders in place of the Client + Source sections when the
              picked service is in the "Transportation" category. Fields
              are stored in `transport_meta` on the appointment doc and
              never touch the customers collection — the tile is a
              driver-roster entry, not a guest visit. */}
          {isTransportationMode && (
            <div className="pt-2 border-t border-border" data-testid="new-appt-transport-form">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Driver Sign Details
              </Label>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-2">
                Transportation booking · not saved to any client profile.
              </p>
              <div className="space-y-2">
                <Input
                  value={transportName}
                  onChange={(e) => setTransportName(e.target.value)}
                  data-testid="new-appt-transport-name"
                  className="h-9"
                  placeholder="Full Name *"
                />
                <Input
                  value={transportHotel}
                  onChange={(e) => setTransportHotel(e.target.value)}
                  data-testid="new-appt-transport-hotel"
                  className="h-9"
                  placeholder="Building / Villa / Hotel *"
                />
                <Input
                  value={transportAddress}
                  onChange={(e) => setTransportAddress(e.target.value)}
                  data-testid="new-appt-transport-address"
                  className="h-9"
                  placeholder="Full Address *"
                />
                <PhoneInput
                  value={transportPhone}
                  onChange={setTransportPhone}
                  testId="new-appt-transport-phone"
                  placeholder="Contact Number *"
                />
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Info className="w-3 h-3 flex-shrink-0" />
                  Treatment Time is picked from the Time field above.
                </p>
              </div>
            </div>
          )}

          {/* ── Source (referral) ── */}
          {!isTransportationMode && (
          <div className="pt-1.5 border-t border-border">
            <div className="flex items-baseline gap-2 flex-wrap">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Source (referred by)
              </Label>
              <p className="text-[10px] text-muted-foreground">
                {sourceLocked
                  ? "Originally referred by the person below. Manage from their profile — no double-award."
                  : "Referrer earns +200 loyalty points the first time it's set."}
              </p>
            </div>
            {sourceLocked ? (
              <div
                className="mt-1 flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-sm"
                data-testid="new-appt-source-locked"
              >
                <div className="flex-1 min-w-0">
                  <span className="font-medium truncate block">
                    {source?.full_name || `${source?.first_name || ""} ${source?.last_name || ""}`.trim() || source?.email || "Customer"}
                  </span>
                  <span className="text-xs text-muted-foreground truncate block">
                    {source?.email || ""}{source?.email && source?.whatsapp ? " · " : ""}{source?.whatsapp || ""}
                  </span>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-background border border-border rounded px-1.5 py-0.5">Locked</span>
              </div>
            ) : (
              <div className="mt-1">
                <CustomerCombobox
                  value={source}
                  onPick={(c) => { if (!c.__new) setSource(c); }}
                  onClear={() => setSource(null)}
                  placeholder="Search by name, email or phone…"
                  excludeId={client && !client.__new ? client.id : null}
                  testId="new-appt-source"
                />
              </div>
            )}
          </div>
          )}

          {/* ── Notes ── */}
          <div>
            <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="new-appt-notes" className="mt-1 text-sm resize-none" />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-2 border-t border-border bg-background">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="new-appt-cancel">Cancel</Button>
          {isWizard && wizardIndex < wizardSteps.length - 1 && (
            <Button
              size="sm"
              variant="outline"
              onClick={continueToNextService}
              disabled={busy}
              data-testid="new-appt-continue"
            >Continue to next service</Button>
          )}
          {/* Bulk "Create N appointments" only surfaces on the FINAL step of a
              wizard — never on the intermediate steps — so the operator is
              forced through the sequential flow. Additionally, disable it
              unless every step (including the active one, whose live pane
              feeds `effectiveServiceId`/`staffId`/`startIso`) has a valid
              service + staff + start time so a half-filled step can't be
              silently POSTed. Non-wizard flows keep the classic Save/Create. */}
          {(!isWizard || wizardIndex === wizardSteps.length - 1) && (() => {
            const allStepsValid = !isWizard ? true : wizardSteps.every((s, i) => (
              i === wizardIndex
                ? Boolean(effectiveServiceId && staffId && startIso)
                : Boolean(s.service_id && s.staff_id && s.start_iso)
            ));
            return (
              <Button
                size="sm"
                onClick={save}
                disabled={busy || !allStepsValid}
                data-testid="new-appt-save"
              >
                {busy ? "Saving…" : (
                  prefill?.editing && wizardSteps.length > 0 && wizardSteps.some((s) => s.id)
                    ? `Save ${wizardSteps.length} appointment${wizardSteps.length === 1 ? "" : "s"}`
                    : (isWizard ? `Create ${wizardSteps.length} appointment${wizardSteps.length === 1 ? "" : "s"}` : (prefill?.editing ? "Save" : "Create"))
                )}
              </Button>
            );
          })()}
        </div>
      </DialogContent>

      <AlertDialog open={!!bookingAlert} onOpenChange={(v) => { if (!v) setBookingAlert(null); }}>
        <AlertDialogContent data-testid="booking-alert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-900">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> Booking alert · {bookingAlert?.name}
            </AlertDialogTitle>
            <AlertDialogDescription className="sr-only">Booking alert: preferences the guest has flagged as important before booking.</AlertDialogDescription>
            <div className="text-foreground mt-2 text-sm" data-testid="booking-alert-text">
              {(bookingAlert?.items || []).map((it, i) => (
                <div
                  key={i}
                  className="whitespace-pre-wrap bg-amber-50 border border-amber-200 rounded-md p-3 mb-2 last:mb-0"
                  data-testid={`booking-alert-item-${i}`}
                >
                  {it.title && <div className="font-medium mb-1">{it.title}</div>}
                  <div>{it.description}</div>
                </div>
              ))}
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="booking-alert-ok">Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
